import { observer } from 'mobx-react-lite'
import { ReactElement, RefObject, useEffect, useState } from 'react'
import BaseDrawer from '../../../../../src/ui-components/modals/BaseDrawer'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore'
import { useResultsStore } from '../../store/ResultsStore'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import { I18n, Translate } from 'common'
import { Box, Grid } from '@mui/material'
import { Thumbnail } from '../../../../../src/ui-components/products/Thumbnail'
import { Print } from '../../../../../src/ui-components/util/Print'
import { GoBackButton } from '../../../../../src/ui-components/util/GoBackButton'
import { ImageType } from 'interfaces'
import ImageZoom from '../../../../../src/ui-components/util/image-zoom'
import { breakpoints } from '../../../../../src/theme/breakpoints'
import { DrawerSizes } from '../../../../../src/theme/drawer-size'
import { Launch } from '@mui/icons-material'
import {
  getSRCForImage,
  getBlobUrlFromBase64,
} from '../../../../../src/helpers/utils'

const ZOOM = 3

export const CatalogDiagramDetailDrawer = observer((): ReactElement => {
  const {
    showDetailCatalogDiagram,
    setShowDetailCatalogDiagram,
    setShowPartsCatalogDiagramDrawer,
  } = useUiStateStore()
  const { selectedCatalogDiagram } = useResultsStore()

  const [printRef, setPrintRef] = useState<RefObject<HTMLDivElement>>()
  const [openImageUrl, setOpenImageUrl] = useState<string>()

  const image = selectedCatalogDiagram?.partImages?.length
    ? (selectedCatalogDiagram?.partImages?.find(
        (i) => i.type === ImageType.LARGE
      ) ??
      selectedCatalogDiagram?.partImages?.find(
        (i) => i.type === ImageType.THUMBNAIL
      ))
    : undefined

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getImageUrl = () => {
    const isBase64 = !image?.url.startsWith('http')
    if (isBase64) {
      return getBlobUrlFromBase64(image?.url)
    } else {
      return image.url
    }
  }

  useEffect(() => {
    if (image !== undefined) {
      const srcUrl = getImageUrl()
      setOpenImageUrl(srcUrl)
    }
  }, [image, getImageUrl])

  const title = (
    <>
      <Grid container>
        <Grid item xs={6}>
          <GoBackButton
            title={Translate('backToAllDiagrams')}
            customHandleGoBack={() => {
              setShowDetailCatalogDiagram(false)
              setShowPartsCatalogDiagramDrawer(true)
            }}
          />
          <div>
            <I18n name="catalogDiagram" />
          </div>
        </Grid>
      </Grid>

      <StyledCloseIcon onClick={() => setShowDetailCatalogDiagram(false)} />
    </>
  )

  const header = (
    <Grid container>
      <HeaderText item xs={12} className="py-2">
        {`${Translate('diagram')} # `}
        <span className="fw-bold">{selectedCatalogDiagram.partNumber} </span>
        {selectedCatalogDiagram.manufacturerName ? (
          <>
            {` | ${Translate('manufacturer')}: `}
            <span className="fw-bold">
              {selectedCatalogDiagram.manufacturerName}
            </span>
          </>
        ) : (
          ''
        )}
      </HeaderText>
      <Grid item xs={12}>
        <div className="d-flex flex-column flex-lg-row">
          <PrintText>
            <Print
              printRef={printRef}
              documentTitle={`${Translate('catalogDiagram')}_${
                selectedCatalogDiagram.partNumber
              }`}
              displayText={Translate('printCatalogDiagram')}
            />
          </PrintText>
          <OpenInNewTab>
            <Anchor href={openImageUrl} target="_blank">
              <Launch style={{ fontSize: '18px', marginRight: '0.2rem' }} />
              <I18n name="openImageInNewTab" />
            </Anchor>
          </OpenInNewTab>
        </div>
      </Grid>
    </Grid>
  )

  const body = (
    <Box>
      <Grid container style={{ overflow: 'hidden' }}>
        <Grid item xs={12} display="flex" justifyContent="center">
          {!image?.url ? (
            <Thumbnail
              urlOrBase64={null}
              width={255}
              height={255}
              placeholder
            />
          ) : (
            <ImageZoom
              src={getSRCForImage(image?.url)}
              zoomFactor={ZOOM}
              alt={`${Translate('diagram')} # ${
                selectedCatalogDiagram.partNumber
              }`}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  )
  return (
    <BaseDrawer
      title={title}
      header={header}
      body={body}
      onClose={() => setShowDetailCatalogDiagram(false)}
      open={showDetailCatalogDiagram}
      getPrintRef={setPrintRef}
      width={window.innerWidth < breakpoints.tabletXLarge ? '100%' : 'auto'}
      height={window.innerWidth < breakpoints.tabletXLarge ? '90vh' : '100%'}
      minWidth={
        window.innerWidth < breakpoints.tabletXLarge
          ? '100%'
          : window.innerWidth > breakpoints.laptopLarge
            ? DrawerSizes.medium
            : 'auto'
      }
      scrollable
    />
  )
})

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  @media print {
    display: none;
  }
`

const PrintText = styled.div`
  font-size: 16px;
`

const OpenInNewTab = styled(PrintText)`
  padding-left: 1rem;
  @media screen and (max-width: ${breakpoints.tablet}px) {
    padding-left: 0rem;
    line-height: 0px;
  }
  @media print {
    display: none;
  }
`

const HeaderText = styled(Grid)`
  font-family: 'Roboto', serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  & span.bold {
    font-weight: 700;
  }
`
const Anchor = styled.a`
  font-family: 'Roboto';
  text-decoration: none;
  color: ${(p) => p.theme.colors.link};
  :hover {
    color: ${(p) => p.theme.colors.link};
  }
`
